// How the tune on screen plays, and where that came from (Tune details):
// your own changes, the house default the owner saved for everyone, or the
// tune's own settings. Reset drops your changes; when the house default moves
// after you changed the tune, it is OFFERED, never applied over your version
// (docs/tune-library-plan.md §3). An admin also sees Save for everyone — the
// button is a courtesy, the database is the lock (migration 0007).

import { useState } from 'react';
import { useAuth } from '@/auth/session';
import { removeTuneDefault, saveTuneDefault } from '@/data/tuneDefaults';
import { canHoldHouseDefault, hasOwnTunePlayback, houseCaptureOf, useStore } from '@/state/store';
import { houseMovedSince } from '@/state/tunePlayback';

export function TunePlaybackDefaults({ onNotice }: { onNotice: (message: string) => void }) {
  const auth = useAuth();
  const tuneKey = useStore((s) => s.currentTuneKey);
  const house = useStore((s) => (s.currentTuneKey ? s.houseDefaults[s.currentTuneKey] : undefined));
  const record = useStore((s) => (s.currentTuneKey ? s.tunePlayback[s.currentTuneKey] : undefined));
  const own = useStore((s) => (s.currentTuneKey ? hasOwnTunePlayback(s, s.currentTuneKey) : false));
  const shareable = useStore((s) => canHoldHouseDefault(s));
  const reset = useStore((s) => s.resetTunePlayback);
  const keep = useStore((s) => s.keepMyTunePlayback);
  const adopt = useStore((s) => s.adoptHouseDefault);
  const drop = useStore((s) => s.dropHouseDefault);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);

  if (!tuneKey) return null;
  const isAdmin = auth.status === 'active' && auth.role === 'admin';
  const moved = own && houseMovedSince(house, record);
  const state = own ? 'Your changes' : house ? 'House default' : 'Default';

  const save = async () => {
    const capture = houseCaptureOf(useStore.getState());
    if (!capture) return;
    setBusy(true);
    setError(null);
    try {
      adopt(await saveTuneDefault(capture));
      onNotice('Saved for everyone');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The default was not saved.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await removeTuneDefault(tuneKey);
      drop(tuneKey);
      onNotice('Default removed');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The default was not removed.');
    } finally {
      setBusy(false);
      setArmed(false);
    }
  };

  return (
    <div className="tune-playback" data-testid="tune-playback">
      <div className="tune-playback-row">
        <span className="tune-playback-label">Playback</span>
        <span className="tune-playback-state" data-testid="tune-playback-state">
          {state}
        </span>
        {own && !moved && (
          <button
            type="button"
            className="btn btn-sm"
            data-testid="tune-playback-reset"
            onClick={reset}
          >
            Reset
          </button>
        )}
      </div>
      {moved && (
        <div className="tune-playback-row" data-testid="tune-playback-moved">
          <span className="tune-playback-state">House default updated</span>
          <button
            type="button"
            className="btn btn-sm"
            data-testid="tune-playback-use-house"
            onClick={reset}
          >
            Use it
          </button>
          <button
            type="button"
            className="btn btn-sm"
            data-testid="tune-playback-keep"
            onClick={keep}
          >
            Keep mine
          </button>
        </div>
      )}
      {isAdmin && shareable && (
        <div className="tune-playback-row">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            data-testid="tune-playback-save"
            disabled={busy}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : house ? 'Update for everyone' : 'Save for everyone'}
          </button>
          {house &&
            (armed ? (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                data-testid="tune-playback-remove-confirm"
                disabled={busy}
                onBlur={() => setArmed(false)}
                onClick={() => void remove()}
              >
                Remove?
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                data-testid="tune-playback-remove"
                disabled={busy}
                onClick={() => setArmed(true)}
              >
                Remove default
              </button>
            ))}
        </div>
      )}
      {error && (
        <p className="tune-document-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
